import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import toast from 'react-hot-toast'
import { useTranslation } from 'react-i18next'
import { useCurrent, useDelete, useFetch } from '../hooks'
import { AlertMsg, ConfirmDelete, MwButton, MwSpinnerButton, NoDataFound } from '../components/ui'
import AttachmentsManager from '../components/ui/AttachmentsManager'
import ProjectInfoCard from '../components/projects/ProjectInfoCard'
import TicketsListItem from '../components/tickets/TicketsListItem'
import TicketStatusChips from '../components/tickets/TicketStatusChips'
import TicketSectionChips from '../components/tickets/TicketSectionChips'
import ProjectSprintsPanel from '../components/projects/ProjectSprintsPanel'
import { normalizeSupportSettings } from '../constants/ticket'
import { apiClient as axios, buildAuthRequestConfig } from '../utils/apiClient'

const TICKETS_PAGE_SIZE = 20;

const ProjectShow = () => {
    const navigate = useNavigate();
    const params = useParams();
    const id = params.id;
    const [t, i18n] = useTranslation('global');
    const { currentLangId, userId } = useCurrent();
    const isAr = currentLangId === 'ar';
    const isRtl = i18n.dir() === 'rtl';
    const [statusFilter, setStatusFilter] = useState('all');
    const [sprintFilter, setSprintFilter] = useState('all');
    const [sectionFilter, setSectionFilter] = useState('all');
    const [tickets, setTickets] = useState([]);
    const [ticketsPagination, setTicketsPagination] = useState({ page: 0, totalPages: 1, total: 0 });
    const [ticketsLoading, setTicketsLoading] = useState(false);
    const [ticketsError, setTicketsError] = useState(false);
    const ticketsRequestRef = useRef(0);
    const ticketsScrollRef = useRef(null);
    const loadMoreRef = useRef(null);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [nowMs, setNowMs] = useState(() => Date.now());

    const { data: projectResult, loading: projectLoading, error: projectError, fetchData: refreshProject } = useFetch(`${process.env.REACT_APP_SUPPORT_API_BASE_URL}/projects/admin/${id}`);
    const { data: sprintsResult, loading: sprintsLoading, error: sprintsError, refreshHandler: refreshSprints } = useFetch(`${process.env.REACT_APP_SUPPORT_API_BASE_URL}/projects/admin/${id}/sprints`);
    // عدّادات الحالات على كل تذاكر المشروع (أو الدورة المختارة)، مش الجزء المحمّل بس.
    const sprintQuery = sprintFilter !== 'all' ? `&sprintId=${encodeURIComponent(sprintFilter)}` : '';
    const sectionQuery = sectionFilter !== 'all' ? `&section=${encodeURIComponent(sectionFilter)}` : '';
    // الحالات بتتعد على القسم المختار؛ الأقسام نفسها بتتعد من غير فلتر القسم عشان كلها تفضل ظاهرة.
    const { data: statsResult, refreshHandler: refreshStats } = useFetch(
        `${process.env.REACT_APP_SUPPORT_API_BASE_URL}/admin/stats?projectId=${encodeURIComponent(id)}${sprintQuery}${sectionQuery}`
    );
    const { data: sectionStatsResult, refreshHandler: refreshSectionStats } = useFetch(
        `${process.env.REACT_APP_SUPPORT_API_BASE_URL}/admin/stats?projectId=${encodeURIComponent(id)}${sprintQuery}`
    );
    const { data: supportSettingsResult } = useFetch(`${process.env.REACT_APP_SUPPORT_API_BASE_URL}/settings`);
    const { data: statusesResult } = useFetch(`${process.env.REACT_APP_SUPPORT_API_BASE_URL}/statuses/admin`);
    const { data: tenantsResult } = useFetch(`${process.env.REACT_APP_Auth_API_BASE_URL}/tenants/getAll`);
    const { data: adminsResult } = useFetch(`${process.env.REACT_APP_Auth_API_BASE_URL}/users/support-admins`);
    const { deleteItem, data: deleteResult, loading: deleteLoading } = useDelete();

    const project = projectResult?.data || null;
    const supportSettings = useMemo(() => ({
        ...normalizeSupportSettings(supportSettingsResult),
        statuses: normalizeSupportSettings({ data: { statuses: statusesResult?.data || [] } }).statuses
    }), [statusesResult, supportSettingsResult]);
    const tenants = Array.isArray(tenantsResult?.data) ? tenantsResult.data : [];
    const admins = useMemo(() => (Array.isArray(adminsResult?.data) ? adminsResult.data : []), [adminsResult]);
    const adminOptions = useMemo(() => admins.map((admin) => ({
        label: admin?.name || admin?.username || admin?._id || '',
        value: admin?._id,
        avatar: admin?.userPic || admin?.avatar || admin?.image || admin?.profileImage || admin?.photo || ''
    })), [admins]);
    const statusStats = statsResult?.data || null;
    const projectTicketsTotal = Number(project?.ticketsCount ?? 0);
    const hasMoreTickets = ticketsPagination.page < ticketsPagination.totalPages;
    const showStatusColumn = statusFilter === 'all';
    const hasSprints = Array.isArray(sprintsResult?.data) && sprintsResult.data.length > 0;

    // تحميل التذاكر على دفعات (lazy loading): الصفحة الأولى عند تغيير الفلتر، والباقي عند الوصول لآخر القائمة.
    const loadTickets = useCallback(async (page) => {
        const requestId = ++ticketsRequestRef.current;
        setTicketsLoading(true);
        setTicketsError(false);
        try {
            const query = new URLSearchParams({ projectId: id, limit: String(TICKETS_PAGE_SIZE), page: String(page) });
            if (statusFilter !== 'all') query.set('status', statusFilter);
            if (sprintFilter !== 'all') query.set('sprintId', sprintFilter);
            if (sectionFilter !== 'all') query.set('section', sectionFilter);
            const response = await axios.get(`${process.env.REACT_APP_SUPPORT_API_BASE_URL}/admin?${query.toString()}`, buildAuthRequestConfig());
            if (requestId !== ticketsRequestRef.current) return;
            const rows = Array.isArray(response?.data?.data) ? response.data.data : [];
            setTickets((current) => {
                if (page === 1) return rows;
                const seen = new Set(current.map((item) => String(item._id)));
                return [...current, ...rows.filter((item) => !seen.has(String(item._id)))];
            });
            setTicketsPagination({
                page,
                totalPages: Math.max(1, Number(response?.data?.pagination?.totalPages || 1)),
                total: Number(response?.data?.pagination?.total || 0)
            });
        } catch {
            if (requestId === ticketsRequestRef.current) setTicketsError(true);
        } finally {
            if (requestId === ticketsRequestRef.current) setTicketsLoading(false);
        }
    }, [id, sectionFilter, sprintFilter, statusFilter]);

    useEffect(() => {
        setTickets([]);
        setTicketsPagination({ page: 0, totalPages: 1, total: 0 });
        if (ticketsScrollRef.current) ticketsScrollRef.current.scrollTop = 0;
        loadTickets(1);
    }, [loadTickets]);

    useEffect(() => {
        const sentinel = loadMoreRef.current;
        if (!sentinel || !hasMoreTickets || ticketsLoading || ticketsError) return undefined;
        const observer = new IntersectionObserver((entries) => {
            if (entries.some((entry) => entry.isIntersecting)) loadTickets(ticketsPagination.page + 1);
        }, { root: ticketsScrollRef.current, rootMargin: '120px' });
        observer.observe(sentinel);
        return () => observer.disconnect();
    }, [hasMoreTickets, loadTickets, ticketsError, ticketsLoading, ticketsPagination.page]);

    const onSprintsChanged = useCallback(() => {
        refreshSprints();
        refreshStats();
        refreshSectionStats();
        loadTickets(1);
    }, [loadTickets, refreshSectionStats, refreshSprints, refreshStats]);

    useEffect(() => {
        const timer = window.setInterval(() => setNowMs(Date.now()), 60 * 1000);
        return () => window.clearInterval(timer);
    }, []);

    useEffect(() => {
        if (deleteResult?.status === 1) {
            toast.success(isAr ? 'تم حذف المشروع' : 'Project deleted');
            navigate(`/${params.tenant}/projects`);
        }
    }, [deleteResult, isAr, navigate, params.tenant]);

    // رفع / حذف ملفات المشروع من صفحة العرض مباشرة (من غير الدخول على التعديل).
    const updateProjectAttachments = useCallback(async (body) => {
        const response = await axios.put(
            `${process.env.REACT_APP_SUPPORT_API_BASE_URL}/projects/admin/${id}/attachments`,
            body,
            buildAuthRequestConfig()
        );
        if (response?.data?.status !== 1) throw new Error(response?.data?.message || 'Failed');
        await refreshProject();
    }, [id, refreshProject]);

    const canRemoveProjectAttachment = useCallback((url) => {
        if (project?.canEdit) return true;
        const entry = (project?.attachmentNames || []).find((item) => item?.url === url);
        return Boolean(userId && entry?.uploadedBy && String(entry.uploadedBy) === String(userId));
    }, [project, userId]);

    const deleteHandler = async () => {
        const attachments = Array.isArray(project?.attachments) ? project.attachments.filter(Boolean) : [];
        if (attachments.length) {
            try {
                await axios.delete(
                    `${process.env.REACT_APP_UPLOADS_API_BASE_URL}/delete`,
                    buildAuthRequestConfig({ data: attachments.length > 1 ? { keyFiles: attachments } : { keyFile: attachments[0] }, validateStatus: () => true })
                );
            } catch {
                // حذف الملفات مش لازم يوقف حذف المشروع.
            }
        }
        deleteItem(`${process.env.REACT_APP_SUPPORT_API_BASE_URL}/projects/admin/${id}`);
    };

    const cols = [
        { key: t('support.tickets.ticketNumber'), width: 100, align: 'center' },
        { key: `${t('support.tickets.subject')} / ${isAr ? 'العميل' : 'Customer'}`, flex: 1, align: isRtl ? 'right' : 'left' },
        { key: isAr ? 'معينة لـ' : 'Assigned To', width: 160, align: isRtl ? 'right' : 'left' },
        { key: `${t('support.tickets.section')} / ${t('support.tickets.product')}`, width: 155, align: isRtl ? 'right' : 'left' },
        // الحالة بتظهر لما الفلتر على "الكل" عشان نعرف حالة كل تذكرة.
        ...(showStatusColumn ? [{ key: t('support.tickets.status'), width: 130, align: 'center' }] : []),
        { key: t('support.tickets.priority'), width: 130, align: 'center' },
        { key: `${t('support.tickets.createdAt')} / ${t('support.tickets.updatedAt')}`, width: 130, align: 'center' },
        { key: isAr ? 'الوقت المتوقع' : 'Expected time', width: 120, align: 'center' },
    ];
    const gridTemplate = cols.map((col) => (col.flex ? '1fr' : `${col.width}px`)).join(' ');
    const gridMinWidth = cols.reduce((sum, col) => sum + (col.flex ? 260 : Number(col.width) || 0), 0) + 8 * (cols.length - 1) + 32;
    const selectedSprint = hasSprints && sprintFilter !== 'all' && sprintFilter !== 'none'
        ? sprintsResult.data.find((item) => String(item._id) === sprintFilter)
        : null;

    if (projectLoading && !project) {
        return (
            <div className="admin-page">
                <div className="admin-panel p-10 flex justify-center"><MwSpinnerButton withLabel={false} isFullCenter /></div>
            </div>
        );
    }

    if (!project) {
        return (
            <div className="admin-page">
                <div className="admin-panel p-4">
                    <AlertMsg type="Error">
                        {projectError?.response?.status === 404 || projectResult
                            ? (isAr ? 'المشروع غير موجود أو ليس لديك صلاحية عرضه' : 'Project not found or you do not have access')
                            : (isAr ? 'تعذر تحميل المشروع' : 'Failed to load project')}
                    </AlertMsg>
                </div>
            </div>
        );
    }

    return (
        <div className="admin-page">
            <div className="admin-panel p-3 md:p-4">
                <div className="flex items-center justify-end gap-2 mb-3 flex-wrap">
                    <MwButton type="saveBtn" onClick={() => navigate(`/${params.tenant}/tickets/add?projectId=${project._id}`)}>
                        {isAr ? 'إنشاء تذكرة للمشروع' : 'New ticket for project'}
                    </MwButton>
                    {project.canEdit && (
                        <MwButton type="secondary" onClick={() => navigate(`/${params.tenant}/projects/edit/${project._id}`)}>
                            {isAr ? 'تعديل المشروع' : 'Edit Project'}
                        </MwButton>
                    )}
                    {project.canDelete && (
                        <MwButton type="deleteBtn" onClick={() => setShowDeleteConfirm(true)}>
                            {isAr ? 'حذف المشروع' : 'Delete Project'}
                        </MwButton>
                    )}
                    <MwButton type="cancelBtn" withIcon={false} onClick={() => navigate(`/${params.tenant}/projects`)}>
                        {isAr ? 'المشاريع' : 'Projects'}
                    </MwButton>
                </div>
                <ProjectInfoCard
                    project={project}
                    supportSettings={supportSettings}
                    lang={currentLangId}
                    admins={admins}
                    attachmentsSlot={(
                        <AttachmentsManager
                            attachments={project.attachments}
                            attachmentNames={project.attachmentNames}
                            isAr={isAr}
                            title={isAr ? 'مرفقات المشروع' : 'Project attachments'}
                            inputId="project_show_attachments"
                            canRemove={canRemoveProjectAttachment}
                            onAdd={(files) => updateProjectAttachments({ add: files.map((file) => file.url), attachmentNames: files })}
                            onRemove={(url) => updateProjectAttachments({ remove: [url] })}
                        />
                    )}
                />
            </div>

            <ProjectSprintsPanel
                projectId={project._id}
                isAr={isAr}
                statuses={supportSettings.statuses}
                sprintsResult={sprintsResult}
                loading={sprintsLoading}
                error={sprintsError}
                canEditProject={Boolean(project.canEdit)}
                onChanged={onSprintsChanged}
                selectedSprint={sprintFilter}
                onSelectSprint={setSprintFilter}
                totalTickets={projectTicketsTotal}
                nowMs={nowMs}
            />

            <div className="admin-panel p-3 md:p-4">
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 15, fontWeight: 900, color: '#0f172a' }}>{isAr ? 'تذاكر المشروع' : 'Project tickets'}</span>
                    <span style={{ minWidth: 26, padding: '2px 8px', borderRadius: 999, background: '#eef2ff', color: '#4f46e5', fontSize: 11, fontWeight: 900, textAlign: 'center' }}>{ticketsPagination.total}</span>
                    {sprintFilter !== 'all' && (
                        <button
                            type="button"
                            onClick={() => setSprintFilter('all')}
                            title={isAr ? 'إلغاء فلتر الدورة' : 'Clear sprint filter'}
                            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '3px 10px', borderRadius: 999, border: '1px solid #a5f3fc', background: '#ecfeff', color: '#0e7490', fontSize: 11, fontWeight: 900, cursor: 'pointer' }}
                        >
                            {sprintFilter === 'none'
                                ? (isAr ? 'بدون دورة' : 'No sprint')
                                : `${isAr ? 'دورة' : 'Sprint'} ${selectedSprint?.sprintNumber ?? ''}${selectedSprint?.title ? ` — ${selectedSprint.title}` : ''}`}
                            <span>✕</span>
                        </button>
                    )}
                    {/* الأقسام اللي فيها تذاكر للمشروع (أو الدورة المختارة) في نفس سطر العنوان، والضغط على قسم بيفلتر بيه. */}
                    <div style={{ marginInlineStart: 'auto' }}>
                        <TicketSectionChips
                            lang={currentLangId}
                            sections={supportSettings.sections}
                            sectionCounts={sectionStatsResult?.data?.sectionCounts}
                            activeSection={sectionFilter}
                            onSectionChange={setSectionFilter}
                        />
                    </div>
                </div>
                <div style={{ marginBottom: 12 }}>
                    <TicketStatusChips
                        lang={currentLangId}
                        statuses={supportSettings.statuses}
                        activeStatus={statusFilter}
                        getStatusCount={(value) => (value === 'all'
                            ? Number(statusStats?.total || 0)
                            : Number(statusStats?.statusCounts?.[value] || 0))}
                        onStatusChange={setStatusFilter}
                    />
                </div>
                <div style={{ background: '#fff', borderRadius: 16, border: '1px solid #e2e8f0', overflow: 'hidden', direction: isRtl ? 'rtl' : 'ltr' }}>
                    {/* ارتفاع ثابت بـ scroll داخلي، والتذاكر بتتحمل على دفعات عند النزول لآخر القائمة. */}
                    <div ref={ticketsScrollRef} className="table-scroll" style={{ overflow: 'auto', maxHeight: 560 }}>
                        <div style={{ minWidth: gridMinWidth }}>
                            <div style={{ display: 'grid', gridTemplateColumns: gridTemplate, padding: '11px 16px', background: '#f8fafc', borderBottom: '2px solid #e2e8f0', gap: 8, position: 'sticky', top: 0, zIndex: 2 }}>
                                {cols.map((col) => (
                                    <div key={col.key} style={{ fontSize: 10, fontWeight: 800, color: '#94a3b8', letterSpacing: 1, textAlign: col.align }}>{col.key}</div>
                                ))}
                            </div>
                            {tickets.map((item) => (
                                <div key={item._id} onClick={() => navigate(`/${params.tenant}/tickets/show/${item._id}`)} style={{ cursor: 'pointer' }}>
                                    <TicketsListItem
                                        lang={currentLangId}
                                        item={item}
                                        gridTemplate={gridTemplate}
                                        supportSettings={supportSettings}
                                        tenants={tenants}
                                        assignees={adminOptions}
                                        nowMs={nowMs}
                                        showStatus={showStatusColumn}
                                        showSprint={sprintFilter === 'all'}
                                    />
                                </div>
                            ))}
                            {ticketsLoading ? (
                                <div style={{ display: 'flex', justifyContent: 'center', padding: tickets.length ? '14px 24px' : '32px 24px' }}>
                                    <MwSpinnerButton withLabel={false} isFullCenter />
                                </div>
                            ) : ticketsError ? (
                                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 8, padding: '18px 24px', fontSize: 12, color: '#b91c1c', fontWeight: 700 }}>
                                    {isAr ? 'تعذر تحميل التذاكر' : 'Failed to load tickets'}
                                    <button type="button" onClick={() => loadTickets(Math.max(1, ticketsPagination.page + 1))} style={{ padding: '4px 10px', borderRadius: 8, border: '1px solid #fecaca', background: '#fff', color: '#b91c1c', cursor: 'pointer', fontWeight: 800 }}>
                                        {isAr ? 'إعادة المحاولة' : 'Retry'}
                                    </button>
                                </div>
                            ) : !tickets.length ? (
                                <div style={{ display: 'flex', justifyContent: 'center', padding: '32px 24px' }}>
                                    <NoDataFound msg={isAr ? 'لا توجد تذاكر مطابقة' : 'No matching tickets'} />
                                </div>
                            ) : hasMoreTickets ? (
                                <div ref={loadMoreRef} style={{ height: 1 }} />
                            ) : (
                                <div style={{ textAlign: 'center', padding: '10px 16px', fontSize: 11, color: '#94a3b8', fontWeight: 700 }}>
                                    {isAr ? `تم عرض كل التذاكر (${tickets.length})` : `All tickets shown (${tickets.length})`}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {project.canDelete && (
                <ConfirmDelete
                    title={isAr ? 'حذف المشروع' : 'Delete Project'}
                    msg={isAr
                        ? 'سيتم حذف المشروع ومرفقاته. التذاكر المرتبطة به لن تُحذف لكن سيتم فك ارتباطها بالمشروع.'
                        : 'The project and its attachments will be deleted. Linked tickets are kept but unlinked from the project.'}
                    onShow={showDeleteConfirm}
                    setOnShow={setShowDeleteConfirm}
                    loading={deleteLoading}
                    onDeleteHandler={deleteHandler}
                    confirmLabel={isAr ? 'حذف المشروع' : 'Delete Project'}
                />
            )}
        </div>
    );
};

export default ProjectShow;
